// src/experiments/uid.js
//
// The unit of assignment. Every visitor gets one `hippo_uid`, kept in a
// first-party cookie with a localStorage mirror. The cookie is the source of
// truth because six code paths call `localStorage.clear()` (logout, "no,
// that's not me", "first time quoting", Ctrl+Shift+R) and none of them touch
// cookies; the mirror exists for the visitor whose browser blocks or expires
// cookies. Whichever side is missing is re-seeded from the other.
//
// Deliberately NOT the quote session id from utils/sessionManager.js: that id
// is rotated and cleared per quote, which would re-bucket people mid-funnel.

const COOKIE_NAME = 'hippo_uid';
const STORAGE_KEY = 'hippo_uid';
const COOKIE_DOMAIN = 'hippo.co.za';
// 400 days is the longest expiry Chrome honours.
const MAX_AGE_SECONDS = 400 * 24 * 60 * 60;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Last resort when neither cookies nor storage can be written (private mode
// with site data blocked). Keeps one id for the life of the page. `persisted`
// records whether a write has ever been read back: once it has, an empty
// cookie and an empty mirror mean the visitor cleared them, and they get a
// fresh id rather than the one this page remembers.
let memoryUid = null;
let persisted = false;

function isValidUid(value) {
  return typeof value === 'string' && UUID_RE.test(value);
}

// A v4 UUID from the platform where possible. `uuid` is only a transitive
// dependency of this project, so it is not imported here.
export function mintUid() {
  const cryptoObj =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  // RFC 4122 version and variant bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
    ''
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// A Domain attribute is only valid on the host it names. On localhost and on
// the Firebase preview channels (*.web.app) a `.hippo.co.za` cookie is silently
// rejected by the browser, so off-domain the cookie is written host-only.
export function isHippoHost(hostname) {
  return hostname === COOKIE_DOMAIN || hostname.endsWith(`.${COOKIE_DOMAIN}`);
}

// Pure, so the per-host rules can be unit-tested without a jsdom URL per case.
export function buildCookieString(uid, { hostname = '', protocol = '' } = {}) {
  const parts = [
    `${COOKIE_NAME}=${uid}`,
    `Max-Age=${MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ];
  if (isHippoHost(hostname)) parts.push(`Domain=.${COOKIE_DOMAIN}`);
  // Secure is dropped by some browsers on plain-http localhost; every real host
  // is https, so keying it on the protocol costs nothing in production.
  if (protocol === 'https:') parts.push('Secure');
  return parts.join('; ');
}

function readCookie() {
  try {
    const pairs = document.cookie ? document.cookie.split(';') : [];
    for (const pair of pairs) {
      const [name, ...rest] = pair.trim().split('=');
      if (name === COOKIE_NAME) return rest.join('=');
    }
  } catch {
    // cookies unavailable
  }
  return null;
}

function writeCookie(uid) {
  try {
    document.cookie = buildCookieString(uid, window.location);
  } catch {
    // cookies unavailable
  }
}

function readStored() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(uid) {
  try {
    localStorage.setItem(STORAGE_KEY, uid);
  } catch {
    // storage blocked
  }
}

// Returns the visitor's uid, creating it on first call. The cookie wins over
// the mirror when both exist; whichever one is missing or stale is rewritten,
// so the id survives everything short of the visitor clearing both.
export function getUid() {
  if (typeof document === 'undefined') {
    if (!memoryUid) memoryUid = mintUid();
    return memoryUid;
  }

  const fromCookie = readCookie();
  const fromStorage = readStored();

  let uid;
  if (isValidUid(fromCookie)) uid = fromCookie;
  else if (isValidUid(fromStorage)) uid = fromStorage;
  else uid = memoryUid && !persisted ? memoryUid : mintUid();

  if (fromCookie !== uid) writeCookie(uid);
  if (fromStorage !== uid) writeStored(uid);
  if (!persisted && (readCookie() === uid || readStored() === uid)) {
    persisted = true;
  }
  memoryUid = uid;
  return uid;
}

// Boot-time entry point. Minting the cookie before anything reads it means it
// starts ageing immediately, so the first real test meets returning visitors
// who are already bucketed. Never throws.
export function ensureUid() {
  try {
    return getUid();
  } catch {
    return null;
  }
}

export const UID_COOKIE_NAME = COOKIE_NAME;
export const UID_STORAGE_KEY = STORAGE_KEY;
